import React from "react";
import { Link } from "react-router-dom";
import { useAdmin } from "./hooks/useAdmin";
import { useUnseenClaims } from "./hooks/useUnseenClaims";
import { usePendingMarketplaceItems } from "./hooks/usePendingMarketplaceItems";
import "./AdminDashboard.css";

export default function AdminDashboard() {
  const { isAdmin, loading, user } = useAdmin();
  const { unseenDancerClaims, unseenStudioClaims } = useUnseenClaims();
  const { pendingCount: pendingMarketplace } = usePendingMarketplaceItems();

  if (loading) {
    return (
      <div style={{ padding: "2rem", textAlign: "center" }}>
        <h2>Loading...</h2>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div style={{ padding: "2rem", textAlign: "center" }}>
        <h2>🔒 Access Denied</h2>
        <p>You don't have permission to access the admin dashboard.</p>
      </div>
    );
  }

  const adminSections = [
    {
      title: "📊 Data Management",
      icon: "📊",
      items: [
        { path: "/upload", label: "Upload Contaste Competition", icon: "📁", description: "Upload new competition results from Contaste" },
        { path: "/upload-contaste-new", label: "Upload Contaste New", icon: "🎭", description: "Upload competition results with full judge marks & round data" },
        { path: "/upload-flymark", label: "Upload Flymark Competition", icon: "📁", description: "Upload new competition results from Flymark" },
        { path: "/upload-flymark-new", label: "Upload Flymark New", icon: "🪰", description: "Upload Flymark results with full per-judge recall data per round" },
        { path: "/history", label: "Upload History", icon: "📜", description: "View history of uploaded competitions" },
        { path: "/competition-archive", label: "Competition Archive", icon: "📁", description: "Browse all competitions in the system" },
      ]
    },
    {
      title: "👥 User & Profile Management",
      icon: "👥",
      items: [
        { path: "/admin/claims", 
          label: "Review Dancer Claims", 
          icon: "👤", 
          description: "Review and approve dancer profile claims",
          badge: unseenDancerClaims
        },
        { 
          path: "/admin/studio-claims", 
          label: "Review Studio Claims", 
          icon: "🏢", 
          description: "Review and approve studio profile claims",
          badge: unseenStudioClaims
        },
        { path: "/admin/users", label: "Registered Users", icon: "👥", description: "View all registered users, their roles, and activity stats" },
        { path: "/merge-dancers", label: "Merge Dancers", icon: "🔄", description: "Merge duplicate dancer profiles" },
        { path: "/merge-studios", label: "Merge Studios", icon: "🏢", description: "Merge duplicate studio profiles" },
        { path: "/studio-transfers", label: "Studio Transfers", icon: "🔄", description: "Manage dancer studio transfers" },
      ]
    },
    {
      title: "⚙️ System Settings",
      icon: "⚙️",
      items: [
        { path: "/admin/visitor-statistics", label: "Visitor Statistics", icon: "📊", description: "View detailed visitor analytics and trends" },
        { path: "/admin/competition-stats", label: "Competition Statistics", icon: "📈", description: "Analyze competition data, participants, and growth trends" },
        { path: "/admin/judges", label: "Judges Directory", icon: "👨‍⚖️", description: "View all judges and which competitions they adjudicated" },
        { path: "/admin/best-days-visibility", label: "Best Days Visibility", icon: "👁️", description: "Control public visibility of Best Days column" },
        { path: "/admin/global-cache", label: "Global Cache Manager", icon: "🚀", description: "Manage and refresh global rankings cache" },
        { path: "/admin/stars", label: "Current Stars", icon: "⭐", description: "View top 3 ranked dancers for every discipline, category and dance" },
        { path: "/admin/duplicates", label: "Manage Duplicates", icon: "🔄", description: "Handle duplicate competition entries" },
        { path: "/admin/sponsorships", label: "Manage Sponsorships", icon: "👟", description: "Manage dancer sponsorships (Prima)" },
      ]
    },
    {
      title: "🏠 Content Management",
      icon: "🏠",
      items: [
        { path: "/homepage-manager", label: "Homepage Manager", icon: "🏠", description: "Manage homepage content and featured items" },
        { path: "/admin/rules", label: "Rules & Regulations", icon: "📋", description: "Edit and publish competition rules, save as PDF with official branding" },
        { path: "/eternal-cup-event-manager", label: "Eternal Cup Event Manager", icon: "🏆", description: "Manage Eternal Cup event page (flyer, info, music, location)" },
        { path: "/admin/music", label: "Music Studio", icon: "🎵", description: "Create and manage music tracks with Suno AI" },
      ]
    },
    {
      title: "🛒 Marketplace",
      icon: "🛒",
      items: [
        { path: "/marketplace", label: "Dance Marketplace", icon: "🛍️", description: "Browse and manage marketplace listings" },
        { 
          path: "/admin/marketplace", 
          label: "Review Listings", 
          icon: "🛡️", 
          description: "Review and approve/reject pending marketplace items",
          badge: pendingMarketplace
        },
      ]
    },
    {
      title: "🔧 Developer Tools",
      icon: "🔧",
      items: [
        { path: "/admin-debug", label: "Firestore Debug Viewer", icon: "🔧", description: "View and debug Firestore data" },
        { path: "/firestore-test", label: "Firestore Test", icon: "🔧", description: "Test Firestore connections and operations" },
      ]
    }
  ];

  return (
    <div className="admin-dashboard">
      <div className="admin-dashboard-header">
        <h1>🛠️ Admin Dashboard</h1>
        <p className="admin-dashboard-subtitle">
          Welcome, <strong>{user?.email}</strong>
        </p>
        <p className="admin-dashboard-description">
          Manage competitions, user profiles, system settings, and content from this central hub.
        </p>
      </div>

      <div className="admin-dashboard-grid">
        {adminSections.map((section) => (
          <div key={section.title} className="admin-section">
            <h2 className="admin-section-title">
              <span className="admin-section-icon">{section.icon}</span>
              {section.title}
            </h2>
            <div className="admin-section-items">
              {section.items.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className="admin-card"
                >
                  <div className="admin-card-header">
                    <span className="admin-card-icon">{item.icon}</span>
                    <span className="admin-card-label">{item.label}</span>
                    {item.badge > 0 && (
                      <span className="admin-card-badge">{item.badge}</span>
                    )}
                  </div>
                  <p className="admin-card-description">{item.description}</p>
                </Link>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="admin-dashboard-footer">
        <div className="admin-quick-stats">
          <div className="admin-stat">
            <span className="admin-stat-icon">👤</span>
            <div className="admin-stat-content">
              <span className="admin-stat-value">{unseenDancerClaims}</span>
              <span className="admin-stat-label">Pending Dancer Claims</span>
            </div>
          </div>
          <div className="admin-stat">
            <span className="admin-stat-icon">🏢</span>
            <div className="admin-stat-content">
              <span className="admin-stat-value">{unseenStudioClaims}</span>
              <span className="admin-stat-label">Pending Studio Claims</span>
            </div>
          </div>
          <div className="admin-stat">
            <span className="admin-stat-icon">🛒</span>
            <div className="admin-stat-content">
              <span className="admin-stat-value">{pendingMarketplace}</span>
              <span className="admin-stat-label">Pending Marketplace</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
